/**
 * Plain-language wording for a refused request, with no dependency on the client so the client itself
 * can use it. `errors.ts` builds on these for screens that know their own field labels.
 */

const isBlank = (v: unknown) => v === undefined || v === null || (typeof v === 'string' && v.trim() === '');

/** Zod v3's default English, restated as the end of "<Label> …". */
export function reasonOf(message: string, value: unknown): string {
  if (message === 'Required' || /^Expected .+, received (null|undefined)$/.test(message)) return 'is required';
  const min = /^String must contain at least (\d+) character/.exec(message);
  if (min) return isBlank(value) || min[1] === '1' ? 'is required' : `must be at least ${min[1]} characters`;
  const max = /^String must contain at most (\d+) character/.exec(message);
  if (max) return `must be at most ${max[1]} characters`;
  const exact = /^String must contain exactly (\d+) character/.exec(message);
  if (exact) return isBlank(value) ? 'is required' : `must be exactly ${exact[1]} characters`;
  if (/^Array must contain at least 1 element/.test(message)) return 'needs at least one selection';
  if (message === 'Invalid uuid') return isBlank(value) ? 'is required' : 'is not a valid selection';
  if (message === 'Invalid') return isBlank(value) ? 'is required' : 'is not in the expected format';
  const num = /^Number must be (greater than|less than|greater than or equal to|less than or equal to) (.+)$/.exec(message);
  if (num) return `must be ${num[1]} ${num[2]}`;
  if (/^Expected number, received/.test(message)) return 'must be a number';
  // A schema's own message (`must be a decimal weight …`) already reads as a reason.
  if (/^(must|is|needs|cannot|should)\b/.test(message)) return message;
  return `— ${message.charAt(0).toLowerCase()}${message.slice(1)}`;
}

/** `grossWeightG` → "Gross weight g": a readable fallback for a field no map names. */
export const humanise = (key: string) => {
  const words = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** Reads a value out of the submitted body by a Zod path, for the blank-vs-malformed call. */
export function valueAt(body: unknown, segments: string[]): unknown {
  let cur: unknown = body;
  for (const s of segments) {
    if (cur === null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[s];
  }
  return cur;
}


/** What a status code means to a person, for when the server sent no sentence of its own. */
export function statusSentence(status: number): string {
  if (status === 400) return 'Some of what was entered was not accepted. Check the form and try again.';
  if (status === 401) return 'Your session has ended. Please sign in again.';
  if (status === 403) return 'You do not have permission to do that. Ask an administrator to grant it.';
  if (status === 404) return 'That could not be found. It may have been removed, or it is not offered on this portal.';
  if (status === 409) return 'That is no longer possible because it has changed since the page was loaded. Refresh and try again.';
  if (status === 413) return 'That is too large to send. Use a smaller file.';
  if (status === 429) return 'Too many attempts. Wait a moment and try again.';
  if (status >= 500) return 'Something went wrong on our side and nothing was changed. Try again in a moment; if it keeps happening, tell support.';
  return 'That did not work. Please try again.';
}

/** Headlines that say nothing: the status line or a generic category rather than a reason. */
const GENERIC = /^(validation failed( \(.*\))?|bad request|conflict|forbidden|unauthorized|not found|internal server error|request failed)\.?$/i;

/**
 * The sentence to show for a refused request. A server message that already gives a reason is kept
 * verbatim; a schema failure is spelled out field by field; anything generic or empty becomes a
 * sentence about what the status means. Never "Validation failed" and never "409 Conflict".
 */
export function plainMessage(status: number, payload: unknown, sent?: unknown): string {
  const p = (payload && typeof payload === 'object' ? payload : {}) as { message?: unknown; errors?: unknown };
  const errors = Array.isArray(p.errors)
    ? (p.errors as Array<{ path?: string; message?: string }>).filter((e) => typeof e?.message === 'string')
    : [];
  if (errors.length > 0) {
    const lines = errors.map(({ path = '(root)', message = '' }) => {
      const segs = path === '(root)' ? [] : path.split('.');
      const key = [...segs].reverse().find((s) => !/^\d+$/.test(s));
      if (!key) return 'The request was incomplete — refresh the page and try again.';
      return `${humanise(key)} ${reasonOf(message, valueAt(sent, segs))}.`;
    });
    return lines.length === 1 ? lines[0]! : ['Please correct:', ...lines.map((l) => `• ${l}`)].join('\n');
  }
  const text = typeof payload === 'string' ? payload
    : typeof p.message === 'string' ? p.message
      : Array.isArray(p.message) ? p.message.join(', ') : '';
  const trimmed = text.trim();
  if (trimmed && !GENERIC.test(trimmed) && !/^\d{3}(\s|$)/.test(trimmed) && !/<\/?[a-z][\s\S]*>/i.test(trimmed)) return trimmed;
  return statusSentence(status);
}
